import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Workflow step: transform `rexel_vendinfc` raw rows into
 * `ap_supplier_warehouse_constraints` domain rows.
 *
 * Stage 2 of the VENDINFC ingest pipeline. Runs after `master_data_bulk_insert`
 * has populated `rexel_vendinfc`. For each row in the latest batch:
 *   - Resolves vendor code → `ap_suppliers.id`
 *   - Resolves warehouse code → `im_warehouses.id`
 *   - Upserts into `ap_supplier_warehouse_constraints`
 *
 * Scoping: `updatedAfter` (typically wired from upstream `startedAt`) limits
 * the rexel_vendinfc rows considered to those touched by the current run.
 */
export const transformVendinfcWarehouseConstraintsStep = defineStepConfig({
  type: "transform_vendinfc_warehouse_constraints",
  name: "Transform VENDINFC → Warehouse Constraints",
  description:
    "Reads rexel_vendinfc rows updated by the current run, FK-resolves vendor + warehouse, and upserts into ap_supplier_warehouse_constraints.",
  category: "Transformation",
  libraryModule: "Procurement",
  deprecated: {
    reason:
      "Use the ingest/CDC supplier warehouse constraint step. Retained for existing workflow versions until their executions are drained.",
    replacedBy: "upsert_supplier_warehouse_constraint",
  },
  inputSchema: z.object({
    updatedAfter: z
      .string()
      .optional()
      .describe(
        "ISO timestamp — only process rows updated after this time. " +
          "Typically wired from the upstream master_data_bulk_insert's startedAt output.",
      ),
  }),
  outputSchema: z.object({
    totalRows: z
      .number()
      .describe("Total number of rexel_vendinfc rows processed"),
    upserted: z
      .number()
      .describe("Number of ap_supplier_warehouse_constraints rows upserted"),
    skippedWarehouseCount: z
      .number()
      .describe(
        "Number of distinct warehouse codes skipped (not found in im_warehouses)",
      ),
    skippedSupplierCount: z
      .number()
      .describe(
        "Number of distinct vendor codes skipped (not found in ap_suppliers)",
      ),
    updatedAfter: z
      .string()
      .optional()
      .describe(
        "Pass-through of the input timestamp so downstream steps can scope to the current run.",
      ),
  }),
  configSchema: z.object({
    batchSize: z
      .number()
      .min(1)
      .max(5000)
      .prefault(500)
      .describe("Number of rows to process per batch"),
  }),
  emits: [] as const,
  execution: {
    startToCloseTimeout: "30 minutes",
    retry: { maximumAttempts: 2 },
  },
});
